<template>
  <div>
    <HiraysHeader inner-page />
    <FloatingSidebar />

    <div class="main">
      <div class="pd-view" v-if="product.id || product.productName">
        <div class="pd-view__crumb locationDiv">
          <div class="wrap">
            <div class="breadcrumbDiv flexsta">
              <i class="font_family icon-shouye"></i>
              <router-link to="/">{{ $t('common.home') }}</router-link>&nbsp;&nbsp;/&nbsp;&nbsp;
              <router-link to="/product/">{{ $t('nav.products') }}</router-link>&nbsp;&nbsp;/&nbsp;&nbsp;
              <router-link :to="'/product/' + routeCategorySlug + '/'">{{ categoryTitle }}</router-link>&nbsp;&nbsp;/&nbsp;&nbsp;
              {{ $cleanBrand(product.productName) }}
            </div>
          </div>
        </div>

        <div class="wrap pd-view__main">
          <div class="pd-view__hero">
            <div class="pd-view__media" v-if="galleryImages.length">
              <div class="swiper-container gallery-top pd-view__gallery">
                <div class="swiper-wrapper">
                  <div class="swiper-slide" v-for="(img, idx) in galleryImages" :key="'g-' + idx">
                    <img :src="img" :alt="product.productName" class="pd-view__gallery-img">
                  </div>
                </div>
                <button type="button" class="pd-view__nav pd-view__nav--prev swiper-button-prev flexcen" v-if="galleryImages.length > 1">
                  <i class="font_family icon-zuojiantou"></i>
                </button>
                <button type="button" class="pd-view__nav pd-view__nav--next swiper-button-next flexcen" v-if="galleryImages.length > 1">
                  <i class="font_family icon-youjiantou"></i>
                </button>
              </div>
              <div class="pd-view__thumbs swiper-container gallery-thumbs" v-if="galleryImages.length > 1">
                <div class="swiper-wrapper">
                  <div class="swiper-slide" v-for="(img, idx) in galleryImages" :key="'t-' + idx">
                    <img :src="img" :alt="product.productName">
                  </div>
                </div>
              </div>
            </div>

            <div class="pd-view__info">
              <router-link :to="'/product/' + routeCategorySlug + '/'" class="pd-view__series">
                {{ categoryTitle }}
              </router-link>
              <h1 class="pd-view__name">{{ $cleanBrand(product.productName) }}</h1>

              <div class="pd-view__meta" v-if="featureItems.length">
                <div class="pd-view__meta-item" v-for="(feat, idx) in featureItems" :key="idx">
                  <span class="pd-view__meta-label">{{ feat.title }}</span>
                  <span class="pd-view__meta-value">{{ feat.desc }}</span>
                </div>
              </div>

              <div class="pd-view__actions">
                <router-link to="/contact/" class="pd-view__cta">
                  <i class="font_family icon-zixun"></i>
                  {{ $t('productDetail.consult') }}
                </router-link>
              </div>
            </div>
          </div>

          <div class="pd-view__toc">
            <button
              type="button"
              class="pd-view__toc-btn"
              :class="{ 'is-active': activeTab === 'item1' }"
              @click="scrollToTab('item1')"
            >{{ $t('productDetail.overview') }}</button>
            <button
              type="button"
              class="pd-view__toc-btn"
              :class="{ 'is-active': activeTab === 'item2' }"
              @click="scrollToTab('item2')"
            >{{ $t('productDetail.specs') }}</button>
          </div>

          <section class="pd-view__block" id="item1" ref="item1">
            <h2 class="pd-view__block-title">{{ $t('productDetail.overview') }}</h2>
            <div class="pd-view__rich" v-if="overviewHtml" v-html="$cleanBrand(overviewHtml)"></div>
            <p v-else class="pd-view__muted">{{ $t('common.noContent') }}</p>
          </section>

          <section class="pd-view__block" id="item2" ref="item2">
            <h2 class="pd-view__block-title">{{ $t('productDetail.specs') }}</h2>
            <div class="pd-view__rich tableDiv" v-if="specsHtml" v-html="$cleanBrand(specsHtml)"></div>
            <p v-else class="pd-view__muted">{{ $t('productDetail.noSpecs') }}</p>
          </section>

          <section class="pd-view__block" v-if="recommended.length">
            <h2 class="pd-view__block-title">{{ $t('productDetail.recommended') }}</h2>
            <div class="pd-view__related">
              <router-link
                v-for="item in recommended"
                :key="item.id"
                :to="'/product/' + routeCategorySlug + '/' + item.id + '.html'"
                class="pd-view__related-item"
              >
                <span class="pd-view__related-media">
                  <img v-if="item.image" :src="item.image" :alt="item.name">
                </span>
                <span class="pd-view__related-name">{{ item.name }}</span>
              </router-link>
            </div>
          </section>
        </div>
      </div>
    </div>

    <HiraysFooter />
  </div>
</template>

<script>
import HiraysHeader from '@/components/hirays/Header.vue'
import HiraysFooter from '@/components/hirays/Footer.vue'
import FloatingSidebar from '@/components/hirays/FloatingSidebar.vue'
import { getProduct, listProductCategory, listProduct } from '@/utils/http'
import { unwrapData, unwrapRows, resolveImageUrl } from '@/utils/content'
import { cmsProductSlug, isSameProductSlug } from '@/constants/productSlugs'
import { setPageTitle } from '@/utils/pageTitle'

export default {
  components: { HiraysHeader, HiraysFooter, FloatingSidebar },
  props: { categorySlug: String, id: [String, Number] },
  data() {
    return {
      product: {},
      categoryName: '',
      routeCategorySlug: '',
      recommended: [],
      activeTab: 'item1',
      gallerySwipers: []
    }
  },
  computed: {
    categoryTitle() {
      return this.$navLabel('productNav', this.categorySlug, this.categoryName || this.$t('nav.products'))
    },
    galleryImages() {
      const urls = (this.product.picUrl || '').split(',').map(s => s.trim()).filter(Boolean)
      return urls.map(resolveImageUrl).filter(Boolean)
    },
    featureItems() {
      const remark = this.$cleanBrand(this.product.productRemark || '')
      if (!remark) return []
      const parts = remark.split(/[;；|]/).map(s => s.trim()).filter(Boolean)
      if (parts.length <= 1) {
        return [{ title: this.$t('common.summary'), desc: remark }]
      }
      return parts.map(text => {
        const seg = text.split(/[:：]/)
        if (seg.length >= 2) {
          return { title: seg[0].trim(), desc: seg.slice(1).join(':').trim() }
        }
        return { title: this.$t('common.summary'), desc: text }
      })
    },
    overviewHtml() {
      const html = this.product.productJieshao || ''
      if (!html) return ''
      if (this.product.productSpecs) return html
      return html.replace(/<table[\s\S]*?<\/table>/gi, '')
    },
    specsHtml() {
      if (this.product.productSpecs) return this.product.productSpecs
      const html = this.product.productJieshao || ''
      const match = html.match(/<table[\s\S]*?<\/table>/i)
      return match ? match[0] : ''
    }
  },
  watch: {
    id: { immediate: true, handler() { this.load() } },
    categorySlug() { this.load() },
    'product.productName'() {
      this.syncDocumentTitle()
    },
    '$i18n.locale'() {
      this.syncDocumentTitle()
    }
  },
  mounted() {
    document.body.classList.add('on-fixed')
    document.body.classList.add('page-inner')
  },
  beforeDestroy() {
    document.body.classList.remove('on-fixed')
    document.body.classList.remove('page-inner')
    this.destroySwipers()
  },
  methods: {
    syncDocumentTitle() {
      const name = this.$cleanBrand(this.product.productName || '')
      if (name) setPageTitle(name)
    },
    reloadCmsData() {
      this.load()
    },
    destroySwipers() {
      if (this.gallerySwipers.length) {
        this.gallerySwipers.forEach(s => { if (s && s.destroy) s.destroy(true, true) })
        this.gallerySwipers = []
      }
    },
    scrollToTab(tabId) {
      this.activeTab = tabId
      const el = this.$refs[tabId]
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    findCategory(cats, slug) {
      return cats.find(c => isSameProductSlug(c.categorySlug, slug)) ||
        cats.find(c => c.categorySlug === cmsProductSlug(slug))
    },
    async load() {
      if (!this.id) return
      this.destroySwipers()

      try {
        const res = await getProduct(this.id)
        this.product = unwrapData(res) || {}
        if (this.product.productName) {
          await this.loadMeta()
          this.$nextTick(() => this.initPageScripts())
          return
        }
      } catch (e) { /* keep empty */ }

      this.product = {}
      this.$nextTick(() => this.initPageScripts())
    },
    async loadMeta(fromStatic) {
      let cat = null
      try {
        const catRes = await listProductCategory({ status: 1, pageNum: 1, pageSize: 100 }, this.$i18n.locale)
        const cats = unwrapRows(catRes)
        cat = this.findCategory(cats, this.categorySlug)
        if (cat) {
          this.categoryName = cat.categoryName
          this.routeCategorySlug = cat.categorySlug
        } else if (fromStatic) {
          this.routeCategorySlug = this.categorySlug
        } else {
          this.routeCategorySlug = cmsProductSlug(this.categorySlug) || this.categorySlug
        }

        const prodRes = await listProduct({
          status: 1,
          categoryId: cat ? cat.id : undefined,
          pageNum: 1,
          pageSize: 200
        }, this.$i18n.locale)
        const products = unwrapRows(prodRes)
        this.recommended = products
          .filter(p => String(p.id) !== String(this.id))
          .slice(0, 4)
          .map(p => ({
            id: p.id,
            name: this.$cleanBrand(p.productName),
            image: resolveImageUrl((p.picUrl || '').split(',')[0])
          }))
      } catch (e) {
        this.routeCategorySlug = this.categorySlug
      }
    },
    initPageScripts() {
      if (window.initNyPage) window.initNyPage()
      if (window.initProductDetailPage) {
        this.destroySwipers()
        this.gallerySwipers = window.initProductDetailPage() || []
      }
    }
  }
}
</script>
